import type { WidgetApiClient, WidgetConfig, ChatStreamCallbacks, PollResponse } from './types';
import { MinimalConfigResponseSchema, ConfigResponseSchema, PollResponseSchema } from './types';

interface WidgetApiClientOptions {
  apiBase: string;
  fetchImpl?: typeof fetch;
}

const defaultClientsBySiteId = new Map<string, WidgetApiClient>();

function resolveUrl(apiBase: string, path: string): string {
  return `${apiBase}${path}`;
}

/**
 * Normalise the flat config response into the WidgetConfig shape expected by
 * the template layer, and extract the session_token separately.
 *
 * Two-step parse: first check `enabled` with the minimal passthrough schema so
 * a `{"enabled": false}` response never reaches the full schema (which requires
 * session_token and name).  Throws with a typed message when the chatbot is
 * disabled so callers can distinguish disabled from network errors.
 */
function parseConfigResponse(data: unknown): { config: WidgetConfig; token: string } {
  const minimal = MinimalConfigResponseSchema.parse(data);
  if (!minimal.enabled) {
    throw new Error('chatbot_disabled');
  }
  const parsed = ConfigResponseSchema.parse(data);
  const config: WidgetConfig = {
    name: parsed.name,
    logo_url: parsed.logo_url,
    message_avatar_url: parsed.message_avatar_url,
    accent_color: parsed.accent_color,
    position: parsed.position,
    welcome_message: parsed.welcome_message,
    suggested_questions: parsed.suggested_questions,
    template: parsed.template,
    turnstile_site_key: parsed.turnstile_site_key,
    turnstile_max_skips: parsed.turnstile_max_skips,
    lead_capture: parsed.lead_capture,
  };
  return { config, token: parsed.session_token };
}

export function createWidgetApiClient({ apiBase, fetchImpl = fetch }: WidgetApiClientOptions): WidgetApiClient {
  const fetchConfig = async (siteId: string): Promise<{ config: WidgetConfig; token: string }> => {
    const res = await fetchImpl(
      resolveUrl(apiBase, `/v1/sites/${encodeURIComponent(siteId)}/widget/config`),
      // no-store bypasses the browser's HTTP cache so config edits apply on the
      // next page load; the response's max-age still lets CDNs absorb fleet traffic.
      { credentials: 'omit', cache: 'no-store' },
    );
    if (!res.ok) {
      throw new Error(`Config fetch failed: ${res.status}`);
    }
    const data: unknown = await res.json();
    return parseConfigResponse(data);
  };

  const sendMessage = (
    siteId: string,
    token: string,
    message: string,
    conversationId: string | null,
    pageUrl: string,
    turnstileToken: string | null,
    callbacks: ChatStreamCallbacks,
  ): AbortController => {
    const controller = new AbortController();

    fetchImpl(resolveUrl(apiBase, `/v1/sites/${encodeURIComponent(siteId)}/chat`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        page_url: pageUrl,
        turnstile_token: turnstileToken,
      }),
      signal: controller.signal,
      credentials: 'omit',
    }).then(async (res) => {
      if (res.status === 409) {
        callbacks.onError('conversation_not_found');
        return;
      }
      if (res.status === 403) {
        const body = await res.json().catch(() => ({})) as { detail?: string };
        if (body.detail === 'turnstile_required' && callbacks.onTurnstileRequired) {
          callbacks.onTurnstileRequired();
          return;
        }
        callbacks.onError(`HTTP 403`);
        return;
      }
      if (!res.ok) {
        callbacks.onError(`HTTP ${res.status}`);
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        callbacks.onError('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          const data = line.slice(6);
          if (data === '[DONE]') {
            callbacks.onDone();
            return;
          }

          try {
            const event = JSON.parse(data) as Record<string, unknown>;
            if (event.error) {
              void reader.cancel();
              callbacks.onError(event.error as string);
              return;
            }
            if (event.handoff === true) {
              // Merchant takeover detected mid-stream — signal handoff, stop reading
              void reader.cancel();
              callbacks.onHandoff();
              return;
            }
            if (typeof event.conversation_id === 'string') {
              callbacks.onConversationId(event.conversation_id);
            }
            if (typeof event.message_id === 'string') {
              callbacks.onMessageId(event.message_id);
            }
            if (typeof event.token === 'string') {
              callbacks.onToken(event.token);
            }
          } catch {
            // Skip malformed JSON lines
          }
        }
      }
      callbacks.onDone();
    }).catch((err: unknown) => {
      if (err instanceof Error && err.name !== 'AbortError') {
        callbacks.onError(err.message || 'Network error');
      }
    });

    return controller;
  };

  const pollMessages = async (
    siteId: string,
    token: string,
    conversationId: string,
    afterMessageId: string | null,
  ): Promise<PollResponse> => {
    const params = afterMessageId ? `?after=${encodeURIComponent(afterMessageId)}` : '';
    const url = resolveUrl(
      apiBase,
      `/v1/sites/${encodeURIComponent(siteId)}/conversations/${encodeURIComponent(conversationId)}/messages${params}`,
    );
    const res = await fetchImpl(url, {
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'omit',
    });
    if (!res.ok) {
      throw new Error(`Poll failed: ${res.status}`);
    }
    const data: unknown = await res.json();
    return PollResponseSchema.parse(data);
  };

  const submitLead = async (
    siteId: string,
    token: string,
    formData: Record<string, string | boolean>,
    pageUrl: string,
  ): Promise<{ lead_id: string; conversation_id?: string }> => {
    const res = await fetchImpl(
      resolveUrl(apiBase, `/v1/sites/${encodeURIComponent(siteId)}/leads`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ data: formData, page_url: pageUrl }),
        credentials: 'omit',
      },
    );
    if (!res.ok) {
      const errData = await res.json().catch(() => ({ detail: 'Failed to submit' })) as { detail?: string };
      throw new Error(errData.detail ?? `Error ${res.status}`);
    }
    return res.json() as Promise<{ lead_id: string; conversation_id?: string }>;
  };

  return { fetchConfig, sendMessage, pollMessages, submitLead };
}

// ---------------------------------------------------------------------------
// Preview-mode API client (postMessage bridge to dashboard iframe parent)
// ---------------------------------------------------------------------------

/**
 * Resolve the target origin for messages sent to the parent frame.
 *
 * We prefer the origin extracted from document.referrer (set by the browser
 * when the iframe is embedded) so the message is scoped to the dashboard that
 * embedded us.  If referrer is absent or unparseable (e.g. about:blank during
 * tests) we fall back to the parent frame's own location origin — same-origin
 * preview pages share the origin, so this is safe.  The bare '*' wildcard is
 * intentionally avoided to prevent information disclosure to third-party pages
 * that might load this script.
 */
function resolveParentOrigin(): string {
  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // Unparseable referrer — fall through
  }
  try {
    return window.parent.location.origin;
  } catch {
    // Cross-origin parent with no referrer — cannot determine origin safely;
    // use the current page's own origin as a last resort (works for same-origin
    // preview setups and is still narrower than '*').
    return window.location.origin;
  }
}

function postMessageRpc<T>(
  requestType: string,
  responseType: string,
  payload: Record<string, unknown>,
  extractResult: (data: Record<string, unknown>) => T,
  timeoutMs: number = 10_000,
): Promise<T> {
  const parentOrigin = resolveParentOrigin();
  return new Promise<T>((resolve, reject) => {
    const reqId = crypto.randomUUID();
    const cleanup = (): void => {
      window.removeEventListener('message', handler);
      clearTimeout(timer);
    };
    const handler = (event: MessageEvent): void => {
      // Only accept replies from the expected parent origin and from the parent
      // window itself (event.source check guards against spoofed same-origin frames).
      if (event.origin !== parentOrigin) return;
      if (event.source !== window.parent) return;
      const evData = event.data as Record<string, unknown>;
      if (evData?.type === responseType && evData.reqId === reqId) {
        cleanup();
        try {
          resolve(extractResult(evData));
        } catch (err) {
          reject(err);
        }
      }
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('postMessage RPC timeout'));
    }, timeoutMs);
    window.addEventListener('message', handler);
    window.parent.postMessage({ type: requestType, reqId, ...payload }, parentOrigin);
  });
}

export function createPreviewApiClient(): WidgetApiClient {
  const fetchConfig = async (): Promise<{ config: WidgetConfig; token: string }> => {
    throw new Error('fetchConfig not used in preview mode');
  };

  const sendMessage = (
    _siteId: string,
    _token: string,
    message: string,
    conversationId: string | null,
    _pageUrl: string,
    _turnstileToken: string | null,
    callbacks: ChatStreamCallbacks,
  ): AbortController => {
    const controller = new AbortController();
    const reqId = crypto.randomUUID();
    const parentOrigin = resolveParentOrigin();

    const handler = (event: MessageEvent): void => {
      if (event.origin !== parentOrigin) return;
      if (event.source !== window.parent) return;
      const evData = event.data as Record<string, unknown>;
      if (evData?.reqId !== reqId) return;

      if (evData.type === 'ch-preview-chat-res') {
        window.removeEventListener('message', handler);
        if (typeof evData.conversation_id === 'string') callbacks.onConversationId(evData.conversation_id);
        if (typeof evData.message_id === 'string') callbacks.onMessageId(evData.message_id);
        callbacks.onToken(evData.answer as string);
        callbacks.onDone();
      }

      if (evData.type === 'ch-preview-chat-err') {
        window.removeEventListener('message', handler);
        callbacks.onError(evData.error as string);
      }
    };

    window.addEventListener('message', handler);
    window.parent.postMessage(
      { type: 'ch-preview-chat-req', reqId, message, conversation_id: conversationId },
      parentOrigin,
    );

    controller.signal.addEventListener('abort', () => {
      window.removeEventListener('message', handler);
    });

    return controller;
  };

  const pollMessages = async (): Promise<PollResponse> => {
    // Poll is a no-op in preview mode — preview sessions are AI-only, no merchant takeover.
    return { controller: 'ai', messages: [] };
  };

  const submitLead = async (
    _siteId: string,
    _token: string,
    formData: Record<string, string | boolean>,
    _pageUrl: string,
  ): Promise<{ lead_id: string; conversation_id?: string }> => {
    return postMessageRpc<{ lead_id: string; conversation_id?: string }>(
      'ch-preview-lead-req',
      'ch-preview-lead-res',
      { form_data: formData },
      (data) => ({
        lead_id: (data.lead_id as string) || '',
        conversation_id: data.conversation_id as string | undefined,
      }),
    );
  };

  return { fetchConfig, sendMessage, pollMessages, submitLead };
}

export function registerWidgetApiClient(siteId: string, client: WidgetApiClient): void {
  defaultClientsBySiteId.set(siteId, client);
}

export function getWidgetApiClient(siteId: string): WidgetApiClient {
  const client = defaultClientsBySiteId.get(siteId);
  if (!client) {
    throw new Error(
      `[ch-widget] No API client registered for siteId "${siteId}". ` +
      'This is a bug — registerWidgetApiClient must be called before rendering.',
    );
  }
  return client;
}
